/**
 * 등록 대상 판정과 「왜 대상이 아닌가」 — 순수 함수.
 *
 * 오너 조건: **AI 검수까지 완료된 것만 등록한다**(2026-08-30).
 * 그런데 AI 분석이 끝나면 그 문제는 **검수대기(100)로 되돌아온다**
 * (OCR_REVIEW_STATUS 주석 「AI 생성 시 재검수로 복귀 포함」). 그래서 「분석 완료」만으로는 부족하고
 * 사람이 그 결과를 **다시 승인**해야 대상이 된다 — review 200 AND analysis 200.
 *
 * **갱신 대상도 함께 센다**: 이미 등록된 행이라도 마지막 등록 뒤 라벨(공통 키워드 포함)이나 내용이
 * 바뀌면 서버가 needs_repromote 를 세운다(BE-20260830 §3). 그 행을 등록에 함께 보내면 서버가 라벨을
 * 다시 계산해 은행에 반영한다 — 「키워드를 고치고 다시 등록하면 반영된다」가 이것이다.
 * 바뀐 것이 없으면 서버가 skipped 의 'unchanged' 로 돌려주므로, 보내도 헛일이 되지 않는다.
 *
 * 이 규칙 때문에 대상이 0건이 되는 일이 흔하다. 그때 화면이 「등록할 수 있는 문제가 없습니다」로만
 * 끝나면 사람은 무엇을 해야 할지 알 수 없다 — 검수를 더 해야 하는지, 분석을 돌려야 하는지,
 * 이미 다 끝난 것인지 갈리지 않는다. 그래서 **나머지가 어느 단계에 있는지 세어 돌려준다.**
 */

/** 단계 키 — 화면이 이 순서대로 그린다(사람이 할 일이 앞) */
export const STAGES = ['rereview', 'analysis', 'review', 'rejected', 'promoted'];

export const STAGE_TEXT = {
  rereview: { label: '재검수 대기', how: 'AI 가 만든 정답·풀이를 아직 승인하지 않았습니다 — ⑤에서 확인하고 승인하세요' },
  analysis: { label: '분석 대기', how: '검수는 끝났습니다 — ⑤에서 AI 분석을 돌리세요' },
  review: { label: '검수 대기', how: '④에서 원본과 대조해 승인하세요' },
  rejected: { label: '반려', how: '④에서 고쳐 다시 승인해야 대상이 됩니다' },
  promoted: { label: '이미 등록됨', how: '문제은행에 있습니다 — 다시 등록하지 않습니다' },
};

/**
 * rows(문제 목록 응답)를 등록 대상과 나머지 단계로 가른다.
 *
 * 반환:
 *   targets      — 지금 등록할 행들(ocr_prob_ids 로 보낼 것)
 *   counts       — 단계별 건수
 *   total        — 전체 행 수
 *   analysisUnknown — analysis_status 가 **모든 행에서** 비어 있다. 목록 응답이 그 필드를 안 준다는
 *                     뜻이라 대상이 영영 0 이 된다. 조용히 0 을 보여 주지 말고 이 사실을 말해야 한다.
 */
export function splitByStage(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const fresh = [];
  const repromote = [];
  const counts = { rereview: 0, analysis: 0, review: 0, rejected: 0, promoted: 0 };
  let known = 0;

  list.forEach((p) => {
    if (p?.analysis_status != null) known += 1;
    if (p?.promoted === true) {
      // 바뀐 것이 있으면 갱신 대상이다. 서버가 라벨을 다시 계산해 반영한다.
      if (p?.needs_repromote === true) repromote.push(p);
      else counts.promoted += 1;
      return;
    }
    const rv = p?.review_status;
    const an = p?.analysis_status;
    if (rv === 200 && an === 200) { fresh.push(p); return; }
    if (rv === 400) { counts.rejected += 1; return; }
    if (an === 200) { counts.rereview += 1; return; } // 분석은 끝났는데 승인 전 — 사람 차례
    if (rv === 200) { counts.analysis += 1; return; } // 검수는 끝났는데 분석 전
    counts.review += 1;
  });

  return {
    fresh,                              // 처음 등록할 것
    repromote,                          // 이미 등록됐지만 라벨·내용이 바뀌어 다시 보낼 것
    targets: [...fresh, ...repromote],  // 등록 버튼이 보낼 전부
    counts,
    total: list.length,
    analysisUnknown: list.length > 0 && known === 0,
  };
}
